import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LocationOnOutlined as LocationIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  ViewQuiltOutlined as FloorPlanIcon,
  Info as InfoIcon,
  ElectricBolt as FlashIcon,
  Wifi as WifiIcon,
  VideocamOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import type { Venue, VenueDocument } from "../types/venues.types";
import { CompletenessScore } from "./CompletenessScore";
import { InfoChip } from "./InfoChip";
import { VenueCardImage } from "../list-venues/VenueCardImage";
import { VenueCardActions } from "../list-venues/VenueCardActions";
import { MapDialog } from "./MapDialog";
import { FloorPlanDialog } from "./FloorPlanDialog";
import { VenueCoverDialog } from "./VenueCoverDialog";
import apiService from "../../../service/Api";

interface VenueCardProps {
  venue: Venue;
  onDelete: (venueId: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  canView: boolean;
  primaryColor: string;
  buttonBackgroundColor: string;
  iconButtonBackgroundColor: string;
  hoverColor: string;
  googleMapsApiKey: string;
  onCoverUpdated?: (newCoverUrl: string) => void;
}

const isImageFile = (fileUrl: string): boolean => {
  return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
};

export const VenueCard: React.FC<VenueCardProps> = ({
  venue,
  onDelete,
  canEdit,
  canDelete,
  canView,
  primaryColor,
  buttonBackgroundColor,
  iconButtonBackgroundColor,
  hoverColor,
  googleMapsApiKey,
  onCoverUpdated,
}) => {
  const navigate = useNavigate();
  const [openMap, setOpenMap] = useState(false);
  const [openFloorPlan, setOpenFloorPlan] = useState(false);
  const [openCoverDialog, setOpenCoverDialog] = useState(false);
  const hasVenue = !!venue?.address_one;

  // Filter for image files
  const imageFiles = venue.files?.filter(
    (file) => file.file_type === "venue_photos",
  );
  const primaryFile = imageFiles?.[0]?.file;

  const floorPlanFiles: VenueDocument[] =
    venue.files?.filter((img) => img.file_type === "floor_plan") || [];
  const floorPlanFile = floorPlanFiles[0] || null;
  const hasFloorPlan = floorPlanFiles.length > 0;

  const handleViewFloorPlan = () => {
    if (!floorPlanFile) return;

    if (isImageFile(floorPlanFile.file)) {
      setOpenFloorPlan(true);
    } else {
      window.open(floorPlanFile.file, "_blank");
    }
  };

  const handleEdit = (venueId: number) => {
    navigate(`/venues/view/${venueId}/edit`);
  };
  const [streamCount, setStreamCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchStreamCount = async () => {
      try {
        const res = await apiService.get<{ stream_count: number }>(
          "/api/streams/stream/stream-venue/",
          { venue: venue.id },
        );
        setStreamCount(res.data.stream_count);
      } catch {
        setStreamCount(null);
      }
    };
    fetchStreamCount();
  }, [venue.id]);
  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            "&:hover .card-actions": {
              opacity: 1,
              pointerEvents: "auto",
            },
            "&:hover .card-image": {
              transform: "scale(1.05)",
            },
          }}
        >
          <VenueCardImage imageUrl={primaryFile} venueName={venue.name} />

          {/* Gradient overlay for better text readability */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "40%",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)",
              pointerEvents: "none",
            }}
          />

          {/* Completeness Score Badge */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 3,
            }}
          >
            <CompletenessScore score={venue?.completeness_score ?? 50} />
          </Box>
          {/* Stream Count Badge */}
          {streamCount !== null && streamCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 12,
                zIndex: 3,
                bgcolor: "#8B4513",
                color: "white",
                width: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: "1px",
                pt: "4px",
                pb: "6px",
                clipPath: "polygon(0 0, 100% 0, 100% 80%, 50% 100%, 0 80%)",
              }}
            >
              <VideocamOutlined sx={{ fontSize: 11 }} />
              <Typography
                sx={{ fontSize: 10, fontWeight: 500, lineHeight: 1.2 }}
              >
                {streamCount}
              </Typography>
            </Box>
          )}
          {/* Edit/Delete overlay */}
          <VenueCardActions
            venueId={venue.id}
            canEdit={canEdit}
            canChangeCover={canEdit}
            canDelete={canDelete}
            primaryColor={primaryColor}
            onEdit={handleEdit}
            onDelete={onDelete}
            onChangeCover={() => setOpenCoverDialog(true)}
          />
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
          {/* Venue Title */}
          <Typography
            variant="h6"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: primaryColor,
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            {venue.name}
          </Typography>

          {/* Address */}
          <Box sx={{ mb: 2 }}>
            <InfoChip
              icon={<LocationIcon />}
              label={venue.address_one || "Address not specified"}
            />
          </Box>

          {/* Venue Details in Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
              mb: 2.5,
            }}
          >
            <InfoChip
              icon={<HomeIcon />}
              label={venue.venue_type || "Type not specified"}
            />
            <InfoChip
              icon={<PeopleIcon />}
              label={venue?.capacity || "Not Available"}
            />
            <InfoChip
              icon={<FlashIcon />}
              label={venue.power_type || "No power"}
            />
            <InfoChip
              icon={<WifiIcon />}
              label={venue.internet_service_provider || "No internet"}
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* Floor Plan Button */}
            {canView && floorPlanFile && (
              <Button
                variant="contained"
                size="small"
                startIcon={<FloorPlanIcon />}
                sx={{
                  backgroundColor: buttonBackgroundColor,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: hoverColor,
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
                disabled={!hasFloorPlan}
                onClick={handleViewFloorPlan}
              >
                Plan
              </Button>
            )}

            <Box sx={{ display: "flex", gap: 1, marginLeft: "auto" }}>
              {/* Location Button */}
              {canView && (
                <Tooltip
                  title={hasVenue ? "View Location" : "No address available"}
                  arrow
                >
                  <span>
                    <IconButton
                      sx={{
                        backgroundColor: hasVenue
                          ? iconButtonBackgroundColor
                          : "#e0e0e0",
                        color: hasVenue ? "white" : "#9e9e9e",
                        borderRadius: 1.5,
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: hasVenue ? hoverColor : "#e0e0e0",
                          transform: hasVenue ? "translateY(-1px)" : "none",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#9e9e9e",
                        },
                      }}
                      onClick={() => {
                        if (hasVenue) {
                          setOpenMap(true);
                        }
                      }}
                      disabled={!hasVenue}
                    >
                      <LocationIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Details Button */}
              {canView && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<InfoIcon />}
                  sx={{
                    backgroundColor: buttonBackgroundColor,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    px: 2,
                    py: 0.5,
                    "&:hover": {
                      backgroundColor: hoverColor,
                      transform: "translateY(-1px)",
                    },
                  }}
                  onClick={() => navigate(`/venues/view/${venue.id}`)}
                >
                  Details
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Map Dialog */}
      <MapDialog
        open={openMap}
        onClose={() => setOpenMap(false)}
        venueName={venue.name}
        address={venue.address_one}
        primaryColor={primaryColor}
        googleMapsApiKey={googleMapsApiKey}
      />

      {/* Floor Plan Dialog */}
      <FloorPlanDialog
        open={openFloorPlan}
        onClose={() => setOpenFloorPlan(false)}
        venueName={venue.name}
        floorPlanFiles={floorPlanFiles}
        primaryColor={primaryColor}
      />

      <VenueCoverDialog
        open={openCoverDialog}
        onClose={() => setOpenCoverDialog(false)}
        images={imageFiles}
        currentCoverUrl={primaryFile}
        onCoverUpdated={(newCoverUrl: string) => {
          onCoverUpdated?.(newCoverUrl);
        }}
      />
    </>
  );
};

export default VenueCard;
